import React, { useEffect, useRef, useState } from 'react';
import { Phone, Mail, MapPin } from 'lucide-react';
import { modelData } from '../data/mock';

const ContactSection = () => {
  const sectionRef = useRef(null);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.2 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => observer.disconnect();
  }, []);

  return (
    <section
      id="contact"
      ref={sectionRef}
      className="relative min-h-[60vh] bg-black py-24 overflow-hidden"
    >
      {/* Background decoration */}
      <div className="absolute inset-0">
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-96 h-96 bg-pink-500/5 rounded-full blur-3xl" />
      </div>

      {/* Section header */}
      <div className="relative max-w-7xl mx-auto px-6 text-center">
        <span className="text-pink-400 text-xs tracking-[0.3em] block mb-4">GET IN TOUCH</span>
        <h2 className={`text-white text-3xl md:text-4xl lg:text-5xl font-extralight tracking-wide mb-6 transition-all duration-1000 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
          CONTACT
        </h2>
        <p className={`text-white/50 text-lg max-w-2xl mx-auto mb-16 transition-all duration-1000 delay-200 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
          Ready to collaborate? Let's create something beautiful together.
        </p>

        {/* Contact info */}
        <div className={`flex flex-col md:flex-row items-center justify-center gap-12 md:gap-24 transition-all duration-1000 delay-400 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
          {/* Phone */}
          <a
            href={`tel:${modelData.contact.phone}`}
            className="group flex flex-col items-center"
          >
            <div className="w-16 h-16 rounded-full border border-pink-400/30 flex items-center justify-center mb-4 group-hover:bg-pink-400/10 group-hover:border-pink-400 transition-all duration-300">
              <Phone className="w-6 h-6 text-pink-400" />
            </div>
            <span className="text-white/40 text-xs tracking-[0.2em] mb-2">PHONE</span>
            <span className="text-white text-lg tracking-wide group-hover:text-pink-400 transition-colors duration-300">
              {modelData.contact.phone}
            </span>
          </a>

          {/* Divider */}
          <div className="hidden md:block w-px h-24 bg-white/10" />

          {/* Email */}
          <a
            href={`mailto:${modelData.contact.email}`}
            className="group flex flex-col items-center"
          >
            <div className="w-16 h-16 rounded-full border border-pink-400/30 flex items-center justify-center mb-4 group-hover:bg-pink-400/10 group-hover:border-pink-400 transition-all duration-300">
              <Mail className="w-6 h-6 text-pink-400" />
            </div>
            <span className="text-white/40 text-xs tracking-[0.2em] mb-2">EMAIL</span>
            <span className="text-white text-lg tracking-wide group-hover:text-pink-400 transition-colors duration-300">
              {modelData.contact.email}
            </span>
          </a>

          {/* Divider */}
          <div className="hidden md:block w-px h-24 bg-white/10" />

          {/* Location */}
          <div className="flex flex-col items-center">
            <div className="w-16 h-16 rounded-full border border-pink-400/30 flex items-center justify-center mb-4">
              <MapPin className="w-6 h-6 text-pink-400" />
            </div>
            <span className="text-white/40 text-xs tracking-[0.2em] mb-2">BASED IN</span>
            <span className="text-white text-lg tracking-wide">
              Florida
            </span>
          </div>
        </div>
      </div>

      {/* Bottom decorative line */}
      <div className="absolute bottom-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-pink-400/30 to-transparent" />
    </section>
  );
};

export default ContactSection;
